import { useEffect, useState } from 'react';
import { apiGet } from '@shared/api/httpClient';
import type { OutreachEvent, OutreachEventsResponse } from '@shared/types/api';
import { formatLocalDateTime } from '@shared/utils/dateTime';
import { groupDemoVisits } from '@/utils/outreachActivity';

export async function getOutreachEvents(invitationId: string, cursor?: string) {
  return apiGet<OutreachEventsResponse>(`/admin/outreach/${encodeURIComponent(invitationId)}/events`, { query: { limit: 200, cursor } });
}

export default function OutreachActivity({ invitationId }: { invitationId: string }) {
  const [events, setEvents] = useState<OutreachEvent[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setBusy(true); setError(''); setEvents([]); setNext(null);
    getOutreachEvents(invitationId).then(response => {
      if (cancelled) return;
      setEvents(response.items); setNext(response.nextToken); setNote(response.coverageNote);
    }).catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load activity.'); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [invitationId, refresh]);
  async function more() {
    if (!next || busy) return;
    setBusy(true); setError('');
    try {
      const response = await getOutreachEvents(invitationId, next);
      setEvents(current => [...current, ...response.items]); setNext(response.nextToken);
    } catch (err) { setError(err instanceof Error ? err.message : 'Unable to load more activity.'); }
    finally { setBusy(false); }
  }
  const visits = groupDemoVisits(events);
  return <section className="outreach-event-activity" aria-label="Demo event activity">
    <h4>Activity: individual visits and events</h4>
    <p className="muted-text">{note || 'Event details are available for accesses captured after the telemetry update.'}</p>
    <p className="muted-text">Token exchange increments the existing open count. Page load is a page render; browser open is JavaScript initialization. None confirms human activity. IP hashes and user agents are correlation hints; forwarded IP headers and browser signals can be spoofed. Times below use your local time; expanded timestamps and CSV use UTC.</p>
    <div className="button-row">
      <button type="button" className="secondary-button" disabled={busy} onClick={() => setRefresh(value => value + 1)}>Refresh activity</button>
      <span>{events.length} loaded events · {visits.length} visit/session groups</span>
    </div>
    {next && <p className="muted-text">Older events remain. Grouping and repeat detection use loaded events and may change when more are loaded.</p>}
    {error && <p role="alert" className="error-text">{error}</p>}
    {!busy && !error && !events.length && <p>No retained event details. Older aggregate opens cannot be reconstructed as individual visits.</p>}
    {visits.map(visit => <details key={visit.id} className="outreach-visit">
      <summary>{formatLocalDateTime(visit.firstAt)} · {visit.classification.replace(/_/g, ' ')} · {visit.pattern} · {visit.events.length} events</summary>
      <p>{visit.reason}</p><p>{visit.patternReason}</p>
      <p className="mono-cell">Session: {visit.sessionId || 'Unavailable'}<br />Visit/group: {visit.id}</p>
      {visit.events.map(event => <details key={event.eventId} className="outreach-event">
        <summary>{formatLocalDateTime(event.occurredAt)} · {event.eventType.replace(/_/g, ' ')}{event.details?.assetId ? ` · ${event.details.assetId}` : ''} · {event.classification.replace(/_/g, ' ')}</summary>
        <dl className="outreach-event-fields">
          {Object.entries({ 'Timestamp UTC': event.occurredAt, 'Event ID': event.eventId,
            'Session ID': event.sessionId, 'Visit ID': event.visitId, 'Client type': event.clientType,
            'User agent': event.userAgent, 'IP hash': event.sourceIpHash, 'IP hash source': event.ipHashSource, 'Request ID': event.requestId,
            'Classification': event.classification, 'Reason': event.classificationReason,
            'Classification version': event.classificationVersion, 'Asset ID': event.details?.assetId,
            'Visibility': event.details?.visibilityState, 'Trusted interaction (client reported)': event.details?.trustedInteraction,
            'WebDriver (client reported)': event.details?.webdriver, 'Legacy uncorrelated': event.details?.legacyUncorrelated,
          }).map(([label, value]) => <div className="detail-row" key={label}><dt>{label}</dt><dd>{value === undefined || value === '' ? 'Unavailable' : String(value)}</dd></div>)}
        </dl>
      </details>)}
    </details>)}
    {busy && <p role="status">Loading activity...</p>}
    {next && <button type="button" className="secondary-button" disabled={busy} onClick={() => void more()}>Load older events</button>}
  </section>;
}
